import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { collection, doc, getDocs, query, setDoc, where, deleteDoc, addDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { Tutorial, CreateTutorialPayload, UpdateTutorialPayload, CreateTutorialSuggestionPayload, TutorialStatus } from "@/types/tutorials";
import { ProductIds } from "@/constants/productIds";
import { pick } from "lodash";

export const tutorialsApi = createApi({
  reducerPath: "tutorialsApi",
  baseQuery: fakeBaseQuery<{ status: number; statusText: string }>(),
  tagTypes: ["Tutorials"],
  endpoints: (builder) => ({
    // For Drawer
    fetchTutorialsByProductIds: builder.query<Tutorial[], ProductIds[]>({
      queryFn: async (productIds) => {
        try {
          if (!productIds || productIds.length === 0) {
            return { data: [] };
          }

          // array-contains-any limits to 10 items
          const activeIds = productIds.slice(0, 10);

          const q = query(
            collection(db, "tutorials"),
            where("isActive", "==", true),
            where("productIds", "array-contains-any", activeIds),
          );

          const snapshot = await getDocs(q);
          const tutorials: Tutorial[] = [];

          snapshot.forEach((docSnap) => {
            tutorials.push({ ...docSnap.data(), id: docSnap.id } as Tutorial);
          });

          return { data: tutorials };
        } catch (error) {
          console.error(error);
          return { error: { status: 500, statusText: "Error fetching tutorials" } };
        }
      },
      providesTags: ["Tutorials"],
      keepUnusedDataFor: 3600, // Cache alto de 1 hora
    }),

    // For Super Admin
    fetchAllTutorials: builder.query<Tutorial[], void>({
      queryFn: async () => {
        try {
          const q = query(collection(db, "tutorials"));
          const snapshot = await getDocs(q);
          const tutorials: Tutorial[] = [];

          snapshot.forEach((docSnap) => {
            tutorials.push({ ...docSnap.data(), id: docSnap.id } as Tutorial);
          });

          return { data: tutorials };
        } catch (error) {
          return { error: { status: 500, statusText: "Error fetching tutorials" } };
        }
      },
      providesTags: ["Tutorials"],
    }),

    createTutorial: builder.mutation<Tutorial, CreateTutorialPayload>({
      queryFn: async (payload, { getState }) => {
        try {
          const state = getState() as any;
          const user = state.auth?.user;

          const newTutorial: Omit<Tutorial, 'id'> = {
            ...payload,
            createdAt: Date.now(),
            createdBy: user?.uid || "system",
          };

          await addDoc(collection(db, "tutorials"), newTutorial);
          return { data: newTutorial };
        } catch (error) {
          return { error: { status: 500, statusText: "Error creating tutorial" } };
        }
      },
      invalidatesTags: ["Tutorials"],
    }),

    updateTutorial: builder.mutation<
      Tutorial,
      {
        id: string;
        payload: UpdateTutorialPayload;
      }
    >({
      queryFn: async ({ id, payload }, { getState }) => {
        try {
          const state = getState() as any;
          const user = state.auth?.user;

          if (!id) {
            return {
              error: {
                status: 400,
                statusText: 'Falta el Id',
              },
            };
          }

          const docRef = doc(db, 'tutorials', id);

          const ALLOWED_FIELDS = [
            'youtubeUrl',
            'title',
            'duration',
            'thumbnailUrl',
            'productIds',
            'status',
            'isActive',
            'message',
            'email',
            'notifyByEmail',
          ];

          const payloadToUpdate = {
            ...pick(payload, ALLOWED_FIELDS),
            lastUpdatedAt: Date.now(),
            lastUpdatedBy: user?.uid ?? 'system',
          };

          await setDoc(docRef, payloadToUpdate, { merge: true });

          return {
            data: {
              id,
              ...payloadToUpdate,
            } as Tutorial,
          };
        } catch (error) {
          console.error('Firebase update error:', error);

          return {
            error: {
              status: 500,
              statusText:
                error instanceof Error
                  ? error.message
                  : 'Error updating tutorial',
            },
          };
        }
      },
      invalidatesTags: ['Tutorials'],
    }),


    deleteTutorial: builder.mutation<string, string>({
      queryFn: async (id) => {
        try {
          await deleteDoc(doc(db, `tutorials/${id}`));
          return { data: id };
        } catch (error) {
          return { error: { status: 500, statusText: "Error deleting tutorial" } };
        }
      },
      invalidatesTags: ["Tutorials"],
    }),

    //fetch only pending sugerencia/Tutorial
    fetchPendingTutorials: builder.query<Tutorial[], void>({
      queryFn: async () => {
        try {
          const q = query(
            collection(db, "tutorials"),
            where("status", "!=", TutorialStatus.Published)
          );

          const snapshot = await getDocs(q);

          const suggestions: Tutorial[] = [];

          snapshot.forEach((docSnap) => {
            suggestions.push({
              ...docSnap.data(),
              id: docSnap.id,
            } as Tutorial);
          });

          return { data: suggestions };
        } catch (error) {
          console.error(error);

          return {
            error: {
              status: 500,
              statusText: "Error fetching tutorial suggestions",
            },
          };
        }
      },
      providesTags: ["Tutorials"],
    }),


    // Para sugerencias hacer una nueva Api para una nueva sugerencia/Tutorial
    createTutorialSuggestion: builder.mutation<Tutorial, CreateTutorialSuggestionPayload>({
      queryFn: async (payload, { getState }) => {
        try {
          const state = getState() as any;
          const user = state.auth?.user;

          const newSuggestion: Omit<Tutorial, "id"> = {
            title: payload.title.trim() || "",
            isActive: false,
            message: payload.message.trim(),
            notifyByEmail: payload.notifyByEmail,
            email: payload.notifyByEmail ? user?.email || "" : "",
            status: TutorialStatus.Pending,
            createdAt: Date.now(),
            createdBy: user?.uid || "system",
          };

          const docRef = await addDoc(collection(db, "tutorials"), newSuggestion);

          return {
            data: {
              id: docRef.id,
              ...newSuggestion,
            },
          };
        } catch (error) {
          console.error(error);

          return {
            error: {
              status: 500,
              statusText: "Error creating tutorial suggestion",
            },
          };
        }
      },
      invalidatesTags: ["Tutorials"],
    }),

  }),
});

export const {
  useFetchTutorialsByProductIdsQuery,
  useLazyFetchTutorialsByProductIdsQuery,
  useFetchAllTutorialsQuery,
  useCreateTutorialMutation,
  useUpdateTutorialMutation,
  useDeleteTutorialMutation,
  useFetchPendingTutorialsQuery,
  useCreateTutorialSuggestionMutation,
} = tutorialsApi;
